import { memo, useState } from "react"
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Stack, Typography } from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { useTranslation } from "react-i18next"
import { AuditEventGroup } from "@/models/auditEvent"
import { humanizeModel } from "@/lib/humanize"
import EventDiff from "./EventDiff"

type Props = {
    group: AuditEventGroup
}

function formatTimestamp(iso: string): string {
    return new Date(iso).toLocaleString()
}

function EventGroup({ group }: Props) {
    const { t } = useTranslation()
    const [expanded, setExpanded] = useState(false)

    const actorLabel = group.actor.display_name
        || group.actor.identifier
        || t("history.actor.system")

    const summary = t("history.group.summary", { count: group.events.length })

    return (
        <Accordion expanded={expanded} onChange={(_, v) => setExpanded(v)}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ "& .MuiAccordionSummary-content": { minWidth: 0, overflow: "hidden" } }}
            >
                <Stack
                    direction="row"
                    spacing={{ xs: 1, sm: 2 }}
                    sx={{ width: "100%", minWidth: 0, alignItems: "center" }}
                >
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={{ xs: 0.25, sm: 2 }}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            alignItems: { xs: "flex-start", sm: "center" },
                        }}
                    >
                        <Typography variant="body2" sx={{ minWidth: { xs: "auto", sm: 140 } }}>
                            {formatTimestamp(group.timestamp)}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                minWidth: 0,
                                wordBreak: "break-word",
                            }}
                        >
                            {actorLabel}
                        </Typography>
                    </Stack>
                    <Chip size="small" label={summary} />
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={2}>
                    {group.events.map(event => {
                        // For duplicate events, show the source record's id —
                        // "Duplicated Employee #15" reads as "the action took
                        // employee #15 as input". The matching create event in
                        // the same group will show the new record's id.
                        const displayId =
                            event.event === "duplicate" && typeof event.metadata?.source_id === "number"
                                ? (event.metadata.source_id as number)
                                : event.auditable_id

                        return (
                            <Box key={event.id} sx={{ borderLeft: 2, borderColor: "divider", pl: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {t("history.eventLine", {
                                        verb: t(`history.events.${event.event}`, event.event),
                                        model: humanizeModel(t, event.auditable_type),
                                        id: displayId,
                                        defaultValue: `{{verb}} {{model}} #{{id}}`,
                                    })}
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                    <EventDiff event={event} />
                                </Box>
                            </Box>
                        )
                    })}
                </Stack>
            </AccordionDetails>
        </Accordion>
    )
}

export default memo(EventGroup)
